import {
  DemoIndicatorColoredIcon,
  GlobalIcon,
  OpenInNew1OutlineIcon,
  TourIndicatorColoredIcon,
} from '@carboncopyco/icons';
import {
  Badge,
  Spacer,
  TextButton,
  Tooltip,
  TooltipBubble,
} from '@carboncopyco/ui';
import classNames from 'classnames';
import type React from 'react';
import { type ReactNode, useRef } from 'react';
import { Screenshot } from '../Screenshot';
import type { SpeakerViewItem } from './SpeakerViewScreen';

export interface SpeakerPanelItemProps {
  item: SpeakerViewItem;
  id: number;
  onSelect?: (isSelected: boolean) => void;
  onDetach: () => void;
  selected?: boolean;
  small?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  dark?: boolean;
  noDrag?: boolean;
  scroll?: boolean;
}

export const speakerPanelIconByAssetType: Record<
  'demo' | 'overlay' | 'tour' | 'url',
  ReactNode
> = {
  demo: <DemoIndicatorColoredIcon size={20} />,
  overlay: <DemoIndicatorColoredIcon size={20} />,
  tour: <TourIndicatorColoredIcon size={20} />,
  url: <GlobalIcon size={20} />,
};
const SCROLL_DISTANCE = 165;

export const SpeakerPanelItem = ({
  item,
  onSelect,
  onDetach,
  selected = false,
  small,
  dark,
  scroll,
  noDrag = false,
}: SpeakerPanelItemProps) => {
  const { presentationName: label, screenshotImageType, image } = item;
  const icon = speakerPanelIconByAssetType[screenshotImageType];
  const rightIcon = <OpenInNew1OutlineIcon size={20} />;
  const speakerItemRef = useRef<HTMLDivElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const getRightItem = () => {
    if (selected) {
      return (
        <div className="absolute right-1 hidden p-[2px] group-hover:block">
          <Badge
            label="CURRENT"
            variant="dark"
          />
        </div>
      );
    }

    return (
      <div className="absolute right-1 hidden p-[2px] group-hover:block">
        {item.canDetach ? (
          <TooltipBubble
            title="Open in a new tab"
            placement="left"
            offset={[0, 15]}
          >
            <div
              className="cursor-pointer p-[2px] rounded-md hover:bg-white hover:bg-opacity-50"
              aria-label="Open in a new tab"
              onClick={(e) => {
                e.stopPropagation();
                onDetach();
              }}
            >
              {rightIcon}
            </div>
          </TooltipBubble>
        ) : (
          <div
            className="cursor-pointer text-xs text-white underline"
            aria-label="go to tab"
          >
            Go to tab
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div
        id={`speaker-panel-item-${item.id}`}
        className="relative cursor-pointer"
        onClick={() => {
          if (selected) {
            return;
          }
          onSelect?.(selected);
        }}
      >
        <div
          className={classNames(
            `relative flex items-center rounded-md whitespace-nowrap group scrollbar scrollbar-thumb-gray-900 scrollbar-track-gray-100 ${
              scroll && 'mr-2'
            } border border-transparent border-solid`,
            {
              'pl-1.5 pr-1.5': icon || rightIcon,
              'px-1.5': !icon && !rightIcon,
              'no-drag': noDrag,
              'hover:bg-ds-natural-50 active:bg-ds-natural-100 active:text-ds-natural-600':
                !dark,
              'bg-ds-accent3-100/50': selected && dark,
              'bg-ds-natural-100 text-ds-natural-700': selected && !dark,
              'text-ds-natural-600': !selected && !dark,

              'bg-none text-ds-accent3-100 hover:bg-white hover:bg-opacity-10 hover:border hover:border-ds-natural-400 hover:border-solid':
                dark,
            },
            small ? 'py-ds-p-5' : 'py-2'
          )}
        >
          <div ref={speakerItemRef}>{icon}</div>

          {label && (
            <>
              <Spacer amount={0.5} />
              <div
                className={'flex w-[240px] '}
                ref={labelRef}
              >
                <TextButton
                  label={label}
                  color="text-current"
                  ellipsis
                />
              </div>
            </>
          )}
          {getRightItem()}
        </div>
      </div>
      <Tooltip
        anchorRef={speakerItemRef}
        placement="top-end"
        noArrow
        interactive={false}
      >
        <div
          className="select-none shrink-0 rounded-md overflow-hidden h-ds-26 relative border-2 border-transparent opacity-50 hover:opacity-75"
          style={{ width: `${SCROLL_DISTANCE}px` }}
        >
          <Screenshot
            noBorder
            image={image}
            alt="screenshot"
            isOverlayDemo={screenshotImageType === 'overlay'}
            isOverlayTour={screenshotImageType === 'tour'}
            isUrl={screenshotImageType === 'url'}
          />
        </div>
      </Tooltip>
      <TooltipBubble
        absolute
        placement="top-start"
        anchorRef={labelRef}
        title={label}
      />
    </>
  );
};

SpeakerPanelItem.displayName = 'SpeakerPanelItem';
